import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const REGION_OPTIONS = [
  { value: "europe",      label: "Flying from Europe",          emoji: "🌍" },
  { value: "asia",        label: "Flying from Asia",            emoji: "🌏" },
  { value: "americas",    label: "Flying from the Americas",    emoji: "🌎" },
  { value: "africa",      label: "Flying from Africa",          emoji: "🌍" },
  { value: "middle_east", label: "Flying from the Middle East", emoji: "🌍" },
  { value: "oceania",     label: "Flying from Oceania",         emoji: "🌏" },
];

const REGION_DISPLAY = {
  all:         "All regions",
  europe:      "Europe",
  asia:        "Asia",
  americas:    "Americas",
  africa:      "Africa",
  middle_east: "Middle East",
  oceania:     "Oceania",
};

function ConfirmPage({ token }) {
  const [state, setState] = useState({ loading: true, ok: false, email: "", error: "" });

  useEffect(() => {
    api.get("/newsletter/confirm", { params: { token } }).then(
      (r) => setState({ loading: false, ok: true, email: r.data.email }),
      (e) => setState({ loading: false, ok: false, error: e.response?.data?.detail || "Failed" })
    );
  }, [token]);

  return (
    <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="newsletter-confirm-page">
      {state.loading ? (
        <p className="eyebrow">// PROCESSING…</p>
      ) : state.ok ? (
        <>
          <p className="eyebrow eyebrow-red">// CONFIRMED</p>
          <h1 className="font-mono text-6xl mt-6 text-white uppercase tracking-tight">ACCESS GRANTED</h1>
          <div className="mt-6 inline-block stamp-corner !relative !top-0 !right-0 !transform-none">
            CLEARANCE · LEVEL 4
          </div>
          <p className="mt-8 text-[#9A8F83] font-mono tracking-[0.14em] text-sm uppercase">
            {state.email} — RESTRICTED DEALS WILL BE DISPATCHED TO YOUR INBOX
          </p>
        </>
      ) : (
        <>
          <p className="eyebrow eyebrow-red">// ERROR</p>
          <h1 className="font-mono text-5xl mt-4 text-white uppercase">Signal invalid</h1>
          <p className="mt-4 text-[#9A8F83] font-mono">{state.error}</p>
        </>
      )}
      <Link to="/" className="btn-rd mt-12 inline-flex">← BACK TO FEED</Link>
    </main>
  );
}

function BeforeYouGoPage({ token }) {
  // status: loading | choosing | saving_prefs | prefs_saved | confirming | unsubscribed | error
  const [status, setStatus] = useState("loading");
  const [regions, setRegions] = useState(["all"]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/newsletter/preferences", { params: { token } })
      .then((r) => {
        setEmail(r.data.email);
        setRegions(r.data.regions || ["all"]);
        setStatus("choosing");
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "Invalid or expired link.");
        setStatus("error");
      });
  }, [token]);

  const toggleRegion = (value) => {
    if (value === "all") {
      setRegions(["all"]);
    } else {
      setRegions((prev) => {
        const filtered = prev.filter((r) => r !== "all" && r !== value);
        const next = prev.includes(value) ? filtered : [...filtered, value];
        return next.length === 0 ? ["all"] : next;
      });
    }
  };

  const savePrefs = async () => {
    setStatus("saving_prefs");
    try {
      await api.post("/newsletter/preferences", { token, regions });
      setStatus("prefs_saved");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save preferences.");
      setStatus("error");
    }
  };

  const confirmUnsub = async () => {
    setStatus("confirming");
    try {
      await api.get("/newsletter/unsubscribe", { params: { token } });
      setStatus("unsubscribed");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to unsubscribe.");
      setStatus("error");
    }
  };

  const regionSummary = regions.includes("all")
    ? "All regions"
    : regions.map((r) => REGION_DISPLAY[r] || r).join(", ");

  if (status === "loading") {
    return (
      <main className="max-w-lg mx-auto px-6 py-32 text-center">
        <p className="eyebrow text-[#555]">// LOADING…</p>
      </main>
    );
  }

  if (status === "error") {
    return (
      <main className="max-w-lg mx-auto px-6 py-32 text-center">
        <p className="eyebrow eyebrow-red">// ERROR</p>
        <h1 className="font-mono text-4xl text-white uppercase tracking-tight mt-4">Signal invalid</h1>
        <p className="mt-4 text-[#9A8F83] font-mono text-sm">{error}</p>
        <Link to="/" className="btn-rd mt-10 inline-flex">← BACK TO DEALS</Link>
      </main>
    );
  }

  if (status === "prefs_saved") {
    return (
      <main className="max-w-lg mx-auto px-4 sm:px-6 py-20 text-center" data-testid="newsletter-unsubscribe-page">
        <p className="eyebrow eyebrow-red">// PREFERENCES UPDATED</p>
        <h1 className="font-mono text-5xl mt-6 text-white uppercase tracking-tight leading-tight">
          Preferences<br />saved.
        </h1>
        <p className="mt-6 text-[#9A8F83] font-sans">
          You'll now receive alerts for: <strong className="text-white">{regionSummary}</strong>
        </p>
        <Link to="/" className="btn-rd mt-10 inline-flex">← BACK TO DEALS</Link>
      </main>
    );
  }

  if (status === "unsubscribed") {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="newsletter-unsubscribe-page">
        <p className="eyebrow eyebrow-red">// REVOKED</p>
        <h1 className="font-mono text-6xl mt-6 text-white uppercase tracking-tight">ACCESS REVOKED</h1>
        <div className="mt-6 inline-block stamp-corner !relative !top-0 !right-0 !transform-none">
          FILE · CLOSED
        </div>
        <p className="mt-8 text-[#9A8F83] font-mono tracking-[0.14em] text-sm uppercase">
          {email} — NO FURTHER TRANSMISSIONS WILL BE SENT
        </p>
        <Link to="/" className="btn-rd mt-12 inline-flex">← BACK TO FEED</Link>
      </main>
    );
  }

  // choosing | saving_prefs | confirming
  const busy = status === "saving_prefs" || status === "confirming";
  return (
    <main className="max-w-lg mx-auto px-4 sm:px-6 py-16" data-testid="newsletter-unsubscribe-page">
      <p className="eyebrow eyebrow-red">// BEFORE YOU GO</p>
      <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight mt-4 leading-tight">
        Before<br />you go.
      </h1>
      <p className="mt-4 text-[#9A8F83] font-sans text-sm leading-relaxed">
        You're about to unsubscribe from Restricted Deals. Would you prefer to
        only receive deals for specific regions instead?
      </p>

      {/* Option A */}
      <div className="mt-10 border border-[#2A2A2A] bg-[#0E0E0E]">
        <div className="px-5 py-4 border-b border-[#2A2A2A]">
          <p className="eyebrow text-[#CC1A1A] text-[10px]">// OPTION A</p>
          <p className="font-mono text-white text-sm tracking-wide mt-1 uppercase">Reduce alerts instead</p>
          <p className="text-[#9A8F83] font-sans text-xs mt-1">Select your departure region — we'll send you deals departing from your chosen region to anywhere in the world.</p>
        </div>
        <div className="px-5 py-4 space-y-2.5">
          {REGION_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={regions.includes(opt.value)}
                onChange={() => toggleRegion(opt.value)}
                className="w-4 h-4 shrink-0 accent-[#CC1A1A]"
                disabled={busy}
              />
              <span className="font-mono text-xs tracking-[0.1em] uppercase text-[#E5E0D8]">
                {opt.emoji} {opt.label}
              </span>
            </label>
          ))}
        </div>
        <div className="px-5 pb-5">
          <button
            onClick={savePrefs}
            disabled={busy || regions.includes("all")}
            className="btn-rd w-full justify-center disabled:opacity-50"
          >
            {status === "saving_prefs" ? "SAVING…" : "SAVE PREFERENCE →"}
          </button>
          {regions.includes("all") && (
            <p className="text-[10px] font-mono text-[#555] mt-2 text-center">
              Select at least one specific region to save
            </p>
          )}
        </div>
      </div>

      {/* Option B */}
      <div className="mt-4 border border-[#2A2A2A] bg-[#0E0E0E]">
        <div className="px-5 py-4 border-b border-[#2A2A2A]">
          <p className="eyebrow text-[#555] text-[10px]">// OPTION B</p>
          <p className="font-mono text-[#9A8F83] text-sm tracking-wide mt-1 uppercase">Unsubscribe completely</p>
          <p className="text-[#555] font-sans text-xs mt-1">Remove me from all Restricted Deals alerts.</p>
        </div>
        <div className="px-5 py-4">
          <button
            onClick={confirmUnsub}
            disabled={busy}
            className="btn-rd-outline w-full justify-center !text-[0.65rem] hover:!border-[#CC1A1A] hover:!text-[#CC1A1A] disabled:opacity-50"
          >
            {status === "confirming" ? "PROCESSING…" : "CONFIRM UNSUBSCRIBE →"}
          </button>
        </div>
      </div>

      <p className="mt-6 text-[10px] font-mono tracking-widest uppercase text-[#444] text-center">{email}</p>
    </main>
  );
}

export default function NewsletterActionPage({ mode }) {
  const [params] = useSearchParams();
  const token = params.get("token");

  if (!token) {
    return (
      <div className="bg-[#111111] min-h-screen">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 py-32 text-center">
          <p className="eyebrow eyebrow-red">// ERROR</p>
          <h1 className="font-mono text-5xl mt-4 text-white uppercase">Missing token</h1>
          <Link to="/" className="btn-rd mt-12 inline-flex">← BACK TO FEED</Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="bg-[#111111] min-h-screen">
      <SiteHeader />
      {mode === "confirm" ? <ConfirmPage token={token} /> : <BeforeYouGoPage token={token} />}
      <SiteFooter />
    </div>
  );
}
